class ScSeeItInAction extends HTMLElement {
    connectedCallback() {
        this.swiperEl = this.querySelector('swiper-container');
        this.toggleBtn = this.querySelector('.sc-see-it-in-action__play-toggle');
        this.isPlaying = true;
        this._currentEndedVideo = null;
        this._endedHandler = null;
        // Flag to suppress onSlideChanged when the chain triggers slideTo itself
        this._autoSliding = false;

        customElements.whenDefined('swiper-container').then(() => {
            this.initSwiper();
            this.initPlayButtons();
            this.bindHoverEvents();
            this.bindToggle();
            this.bindNavigation();
            this.bindPopupClicks();
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.swiperEl?.swiper?.slideTo(0, 0, false);
                    this.updateNavState();
                    setTimeout(() => this.startAutoplay(), 150);
                });
            });
        });
    }

    // ── Swiper ──────────────────────────────────────────────────────────────

    initSwiper() {
        if (!this.swiperEl) return;

        Object.assign(this.swiperEl, {
            slidesPerView: '2.2',
            spaceBetween: 12,
            initialSlide: 0,
            on: {
                init: () => this.updateNavState(),
                slideChangeTransitionEnd: () => {
                    this.onSlideChanged();
                    this.updateNavState();
                },
            },
            breakpoints: {
                600: {
                    slidesPerView: '3.5',
                },
                991: {
                    slidesPerView: '2.5',
                },
                1200: {
                    slidesPerView: '3.5',
                }
            },
        });

        this.swiperEl.initialize();
        this.swiperEl.classList.add('sc-ready');
    }

    // ── Per-slide play button ─────────────────────────────────────────────────

    // Injects a centered play-button overlay into every video wrapper and keeps
    // it in sync with actual video playback via play/pause events.
    initPlayButtons() {
        this.querySelectorAll('.sc-see-it-in-action__video-wrapper').forEach(wrapper => {
            const btn = document.createElement('div');
            btn.className = 'sc-see-it-in-action__slide-play-btn';
            btn.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 3.5l14 8.5-14 8.5z"/></svg>`;
            wrapper.appendChild(btn);

            const video = wrapper.querySelector('.sc-see-it-in-action__video');
            if (video) {
                video.addEventListener('play',  () => wrapper.classList.add('is-playing'));
                video.addEventListener('pause', () => wrapper.classList.remove('is-playing'));
            }
        });
    }

    // ── Autoplay core ─────────────────────────────────────────────────────────

    // Attach an 'ended' listener to a specific video.
    // On ended: directly plays the next video by index WITHOUT waiting for
    // slideChangeTransitionEnd — because with slidesPerView:'auto' and all
    // slides in viewport Swiper won't actually scroll, so that event never fires.
    _attachEnded(video) {
        if (this._currentEndedVideo && this._endedHandler) {
            this._currentEndedVideo.removeEventListener('ended', this._endedHandler);
        }
        this._currentEndedVideo = video;

        const allSlides = Array.from(this.swiperEl.querySelectorAll('swiper-slide'));
        const slideIndex = allSlides.indexOf(video.closest('swiper-slide'));
        const lastIndex  = allSlides.length - 1;

        this._endedHandler = () => {
            if (!this.isPlaying) return;
            const swiper = this.swiperEl?.swiper;
            if (!swiper) return;

            const nextIndex = slideIndex >= lastIndex ? 0 : slideIndex + 1;
            const nextVideo = allSlides[nextIndex]?.querySelector('.sc-see-it-in-action__video');
            if (!nextVideo) return;

            // Reset every video before playing the next one
            this.querySelectorAll('.sc-see-it-in-action__video').forEach(v => {
                v.pause();
                v.currentTime = 0;
            });

            // Move the slider visually; mark as auto so onSlideChanged ignores it
            this._autoSliding = true;
            swiper.slideTo(nextIndex);

            // Play next video directly — no event dependency
            this._attachEnded(nextVideo);
            nextVideo.play().catch(() => {});
        };

        video.addEventListener('ended', this._endedHandler, { once: true });
    }

    // Pause + reset every video and tear down the ended listener.
    _stopAll() {
        if (this._currentEndedVideo && this._endedHandler) {
            this._currentEndedVideo.removeEventListener('ended', this._endedHandler);
            this._currentEndedVideo = null;
            this._endedHandler = null;
        }
        this.querySelectorAll('.sc-see-it-in-action__video').forEach(v => {
            v.pause();
            v.currentTime = 0;
        });
    }

    startAutoplay() {
        if (!this.isPlaying) return;
        const video = this.getActiveVideo();
        if (!video) return;
        this._attachEnded(video);
        video.play().catch(() => {});
    }

    // Called by slideChangeTransitionEnd.
    // Only acts on MANUAL navigation (prev/next buttons).
    // Auto-chain triggered slideTo calls are suppressed via _autoSliding.
    onSlideChanged() {
        if (this._autoSliding) {
            this._autoSliding = false;
            return;
        }
        this._stopAll();
        if (this.isPlaying) this.startAutoplay();
    }

    // ── Hover ─────────────────────────────────────────────────────────────────
    // isPlaying ON:
    //   mouseover → interrupt current video, play hovered one.
    //   mouseout  → do nothing; video keeps playing, chain continues from it.
    //
    // isPlaying OFF:
    //   mouseover → play video.
    //   mouseout  → pause + reset video.

    bindHoverEvents() {
        if (!this.swiperEl) return;

        this.swiperEl.addEventListener('mouseover', (e) => {
            const wrapper = e.target.closest('.sc-see-it-in-action__video-wrapper');
            if (!wrapper) return;
            const video = wrapper.querySelector('.sc-see-it-in-action__video');
            if (!video) return;

            if (this.isPlaying) {
                if (this._currentEndedVideo === video) return;
                this._stopAll();
                this._attachEnded(video);
                video.play().catch(() => {});
            } else {
                video.play().catch(() => {});
            }
        });

        this.swiperEl.addEventListener('mouseout', (e) => {
            if (this.isPlaying) return; // chain owns the video; don't interfere
            const wrapper = e.target.closest('.sc-see-it-in-action__video-wrapper');
            if (!wrapper || wrapper.contains(e.relatedTarget)) return;
            const video = wrapper.querySelector('.sc-see-it-in-action__video');
            if (!video) return;
            video.pause();
            video.currentTime = 0;
        });
    }

    // ── Play / Pause toggle ───────────────────────────────────────────────────

    bindToggle() {
        this.toggleBtn?.addEventListener('click', () => {
            this.isPlaying = !this.isPlaying;
            this.toggleBtn.classList.toggle('is-playing', this.isPlaying);
            if (this.isPlaying) {
                this.startAutoplay();
            } else {
                this._stopAll();
            }
        });
    }

    // ── Navigation buttons ────────────────────────────────────────────────────

    bindNavigation() {
        this.querySelector('.sc-see-it-in-action__nav-btn--prev')
            ?.addEventListener('click', () => this.swiperEl?.swiper?.slidePrev());
        this.querySelector('.sc-see-it-in-action__nav-btn--next')
            ?.addEventListener('click', () => this.swiperEl?.swiper?.slideNext());
    }

    updateNavState() {
        const swiper = this.swiperEl?.swiper;
        if (!swiper) return;
        const prev = this.querySelector('.sc-see-it-in-action__nav-btn--prev');
        const next = this.querySelector('.sc-see-it-in-action__nav-btn--next');
        if (prev) prev.classList.toggle('is-hidden', swiper.isBeginning);
        if (next) next.classList.toggle('is-hidden', swiper.isEnd);
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    getActiveVideo() {
        return this.swiperEl?.querySelector(
            '.swiper-slide-active .sc-see-it-in-action__video'
        ) ?? null;
    }

    // ── Video popup ──────────────────────────────────────────────────────────

    bindPopupClicks() {
        if (!this.swiperEl) return;
        this.swiperEl.addEventListener('click', (e) => {
            const slide = e.target.closest('swiper-slide');
            if (!slide) return;
            const allSlides = Array.from(this.swiperEl.querySelectorAll('swiper-slide'));
            const index = allSlides.indexOf(slide);
            if (index >= 0) this.openPopup(index);
        });
    }

    openPopup(index) {
        this._stopAll();
        if (!this._popup) this._buildPopup();

        this._popup.hidden = false;
        document.body.classList.add('sc-sia-popup-open');
        this._popupMuted = false;

        const muteBtn = this._popup.querySelector('.sc-sia-popup__mute-btn');
        if (muteBtn) muteBtn.classList.remove('is-muted');

        requestAnimationFrame(() => {
            const swiper = this._popupSwiperEl?.swiper;
            if (swiper) swiper.slideTo(index, 0, false);
            this._playPopupSlide(index);
            this._updatePopupInfo(index);
            this._updatePopupNavState();
        });
    }

    closePopup() {
        if (!this._popup) return;
        if (this._progressRaf) {
            cancelAnimationFrame(this._progressRaf);
            this._progressRaf = null;
        }
        this._popup.querySelectorAll('video').forEach(v => {
            v.pause();
            v.currentTime = 0;
        });
        this._popup.hidden = true;
        document.body.classList.remove('sc-sia-popup-open');
        if (this.isPlaying) this.startAutoplay();
    }

    _buildPopup() {
        const slides = Array.from(this.swiperEl.querySelectorAll('swiper-slide'));

        // ── Collect slide data from the horizontal slider into a JS array ──
        // Using getAttribute() directly on the source elements for reliability,
        // since data attributes on <swiper-slide> custom elements may not
        // survive DOM operations consistently across all browsers.
        this._popupSlideData = [];
        const videoSources = [];

        slides.forEach(slide => {
            const video = slide.querySelector('video');
            const source = video?.querySelector('source');
            if (!video || !source) return;

            this._popupSlideData.push({
                modelName:    slide.getAttribute('data-model-name') || '',
                variantId:    slide.getAttribute('data-variant-id') || '',
                productTitle: slide.getAttribute('data-product-title') || '',
                swatchName:   slide.getAttribute('data-swatch-name') || '',
                swatchImage:  slide.getAttribute('data-swatch-image') || '',
                productImage: slide.getAttribute('data-product-image') || '',
                productPrice: slide.getAttribute('data-product-price') || '',
                productHandle:slide.getAttribute('data-product-handle') || '',
                available:    slide.getAttribute('data-available') !== 'false',
            });

            videoSources.push({
                src:    source.getAttribute('src') || '',
                type:   source.getAttribute('type') || 'video/mp4',
                poster: (video.getAttribute('poster') || '').replace(/width=\d+/, 'width=800'),
            });
        });

        // ── Build popup DOM ────────────────────────────────────────────────
        const popup = document.createElement('div');
        popup.className = 'sc-sia-popup';
        popup.hidden = true;
        popup.innerHTML = `
            <div class="sc-sia-popup__backdrop"></div>
            <div class="sc-sia-popup__content">
                <swiper-container class="sc-sia-popup__slider" init="false"></swiper-container>

                <button class="sc-sia-popup__close-btn" aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="white" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>

                <div class="sc-sia-popup__right-controls">
                    <button class="sc-sia-popup__mute-btn" aria-label="Mute">
                        <svg class="sc-sia-popup__icon-muted" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <line x1="23" y1="9" x2="17" y2="15" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                            <line x1="17" y1="9" x2="23" y2="15" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <svg class="sc-sia-popup__icon-unmuted" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M15.54 8.46a5 5 0 010 7.07" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                            <path d="M19.07 4.93a10 10 0 010 14.14" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                    </button>
                    <button class="sc-sia-popup__play-btn is-playing" aria-label="Pause">
                        <svg class="sc-sia-popup__icon-pause" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="6" y="4" width="4" height="16" rx="1" fill="white"/>
                            <rect x="14" y="4" width="4" height="16" rx="1" fill="white"/>
                        </svg>
                        <svg class="sc-sia-popup__icon-play" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M5 3l14 9-14 9V3z" fill="white"/>
                        </svg>
                    </button>
                </div>

                <button class="sc-sia-popup__nav sc-sia-popup__nav--up" aria-label="Previous video">
                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                        <path d="M13 7L7 1L1 7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                <button class="sc-sia-popup__nav sc-sia-popup__nav--down" aria-label="Next video">
                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                        <path d="M1 1L7 7L13 1" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <div class="sc-sia-popup__bottom">
                    <button class="sc-sia-popup__info-toggle" aria-label="Toggle product info">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 12.5l5-5 5 5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <div class="sc-sia-popup__model-name"></div>
                    <div class="sc-sia-popup__product-card">
                        <img class="sc-sia-popup__product-img" src="" alt="">
                        <div class="sc-sia-popup__product-meta">
                            <span class="sc-sia-popup__product-name"></span>
                            <div class="sc-sia-popup__swatch-row">
                                <span class="sc-sia-popup__swatch-dot"></span>
                                <span class="sc-sia-popup__swatch-label"></span>
                            </div>
                            <span class="sc-sia-popup__product-price"></span>
                        </div>
                        <button class="sc-sia-popup__atc-btn">ADD TO<br>BAG</button>
                    </div>
                </div>
            </div>`;

        // ── Populate vertical swiper slides ────────────────────────────────
        const swiperEl = popup.querySelector('.sc-sia-popup__slider');

        videoSources.forEach((vs, i) => {
            const popupSlide = document.createElement('swiper-slide');
            popupSlide.className = 'sc-sia-popup__slide';
            popupSlide.setAttribute('data-slide-index', i);
            popupSlide.innerHTML = `
                <div class="sc-sia-popup__progress" aria-label="Video progress">
                    <div class="sc-sia-popup__progress-fill"></div>
                </div>
                <video class="sc-sia-popup__video" playsinline preload="none"
                    ${vs.poster ? `poster="${vs.poster}"` : ''}>
                    <source src="${vs.src}" type="${vs.type}">
                </video>`;
            swiperEl.appendChild(popupSlide);
        });

        // ── Init vertical swiper ───────────────────────────────────────────
        Object.assign(swiperEl, {
            direction: 'vertical',
            slidesPerView: 1,
            spaceBetween: 0,
            on: {
                slideChangeTransitionEnd: () => {
                    const s = swiperEl.swiper;
                    if (!s) return;
                    this._playPopupSlide(s.activeIndex);
                    this._updatePopupInfo(s.activeIndex);
                    this._updatePopupNavState();
                },
            },
        });
        swiperEl.initialize();
        this._popupSwiperEl = swiperEl;

        this._bindPopupEvents(popup);
        document.body.appendChild(popup);
        this._popup = popup;
    }

    _bindPopupEvents(popup) {
        popup.querySelector('.sc-sia-popup__backdrop')
            .addEventListener('click', () => this.closePopup());
        popup.querySelector('.sc-sia-popup__close-btn')
            .addEventListener('click', () => this.closePopup());

        // ESC key
        this._popupEscHandler = (e) => {
            if (e.key === 'Escape' && this._popup && !this._popup.hidden) {
                this.closePopup();
            }
        };
        document.addEventListener('keydown', this._popupEscHandler);

        // Mute toggle
        const muteBtn = popup.querySelector('.sc-sia-popup__mute-btn');
        muteBtn.addEventListener('click', () => {
            this._popupMuted = !this._popupMuted;
            muteBtn.classList.toggle('is-muted', this._popupMuted);
            const v = this._getActivePopupVideo();
            if (v) v.muted = this._popupMuted;
        });

        // Play/pause toggle
        const playBtn = popup.querySelector('.sc-sia-popup__play-btn');
        this._popupPlaying = true;
        playBtn.addEventListener('click', () => {
            this._popupPlaying = !this._popupPlaying;
            playBtn.classList.toggle('is-playing', this._popupPlaying);
            const v = this._getActivePopupVideo();
            if (v) {
                if (this._popupPlaying) v.play().catch(() => {});
                else v.pause();
            }
        });

        // Info panel toggle
        const infoToggle = popup.querySelector('.sc-sia-popup__info-toggle');
        const bottom = popup.querySelector('.sc-sia-popup__bottom');
        infoToggle.addEventListener('click', () => {
            bottom.classList.toggle('is-collapsed');
            infoToggle.classList.toggle('is-collapsed');
        });

        // Vertical nav arrows
        popup.querySelector('.sc-sia-popup__nav--up')
            ?.addEventListener('click', () => this._popupSwiperEl?.swiper?.slidePrev());
        popup.querySelector('.sc-sia-popup__nav--down')
            ?.addEventListener('click', () => this._popupSwiperEl?.swiper?.slideNext());

        // Click on video to toggle play/pause
        popup.querySelector('.sc-sia-popup__slider')
            ?.addEventListener('click', (e) => {
                // Ignore clicks on controls / bottom panel
                if (e.target.closest('.sc-sia-popup__bottom') ||
                    e.target.closest('.sc-sia-popup__progress')) return;
                this._popupPlaying = !this._popupPlaying;
                playBtn.classList.toggle('is-playing', this._popupPlaying);
                const v = this._getActivePopupVideo();
                if (v) {
                    if (this._popupPlaying) v.play().catch(() => {});
                    else v.pause();
                }
            });

        // Clickable progress bar — seek on click/drag
        popup.addEventListener('click', (e) => {
            const bar = e.target.closest('.sc-sia-popup__progress');
            if (!bar) return;
            const video = this._getActivePopupVideo();
            if (!video || !video.duration) return;
            const rect = bar.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            video.currentTime = ratio * video.duration;
        });

        // Add to cart
        popup.querySelector('.sc-sia-popup__atc-btn')
            .addEventListener('click', () => this._popupAddToCart());
    }

    _playPopupSlide(index) {
        // Pause + reset every popup video, reset all progress bars
        const allVideos = this._popup.querySelectorAll('.sc-sia-popup__video');
        allVideos.forEach(v => { v.pause(); v.currentTime = 0; });
        this._popup.querySelectorAll('.sc-sia-popup__progress-fill')
            .forEach(f => { f.style.width = '0%'; });

        // Kill previous rAF loop
        if (this._progressRaf) {
            cancelAnimationFrame(this._progressRaf);
            this._progressRaf = null;
        }

        const video = allVideos[index];
        if (!video) return;

        video.muted = this._popupMuted;
        this._popupPlaying = true;
        const playBtn = this._popup.querySelector('.sc-sia-popup__play-btn');
        if (playBtn) playBtn.classList.add('is-playing');

        // Progress bar update loop
        const fill = this._popup.querySelectorAll('.sc-sia-popup__progress-fill')[index];
        const updateProgress = () => {
            if (fill && video.duration) {
                fill.style.width = `${(video.currentTime / video.duration) * 100}%`;
            }
            this._progressRaf = requestAnimationFrame(updateProgress);
        };
        this._progressRaf = requestAnimationFrame(updateProgress);

        // Try to play with sound; fall back to muted if autoplay policy blocks it
        video.play().catch(() => {
            if (!this._popupMuted) {
                this._popupMuted = true;
                video.muted = true;
                const mb = this._popup.querySelector('.sc-sia-popup__mute-btn');
                if (mb) mb.classList.add('is-muted');
                video.play().catch(() => {});
            }
        });

        // Auto-advance when video ends
        video.onended = () => {
            if (!this._popupPlaying) return;
            const swiper = this._popupSwiperEl?.swiper;
            if (!swiper) return;
            const total = this._popupSlideData.length;
            const next = index < total - 1 ? index + 1 : 0;
            if (next === index) {
                video.currentTime = 0;
                video.play().catch(() => {});
            } else {
                swiper.slideTo(next);
            }
        };
    }

    // Reads product info from the JS data array (not DOM) for reliability
    _updatePopupInfo(index) {
        const d = this._popupSlideData?.[index];
        if (!d) return;

        const bottom = this._popup.querySelector('.sc-sia-popup__bottom');

        bottom.querySelector('.sc-sia-popup__model-name').textContent =
            d.modelName ? `@${d.modelName}` : '';
        bottom.querySelector('.sc-sia-popup__product-name').textContent =
            d.productTitle || '';
        bottom.querySelector('.sc-sia-popup__swatch-label').textContent =
            d.swatchName || '';
        bottom.querySelector('.sc-sia-popup__product-price').textContent =
            d.productPrice || '';

        const img = bottom.querySelector('.sc-sia-popup__product-img');
        if (d.productImage) {
            img.src = d.productImage;
            img.alt = d.productTitle || '';
            img.style.display = '';
        } else {
            img.style.display = 'none';
        }

        const dot = bottom.querySelector('.sc-sia-popup__swatch-dot');
        if (d.swatchImage) {
            dot.style.backgroundImage = `url('${d.swatchImage}')`;
            dot.style.display = '';
        } else {
            dot.style.display = 'none';
        }

        this._popupCurrentVariantId = d.variantId;

        // ATC button — out of stock state
        const atcBtn = bottom.querySelector('.sc-sia-popup__atc-btn');
        if (d.available) {
            atcBtn.disabled = false;
            atcBtn.innerHTML = 'ADD TO<br>BAG';
        } else {
            atcBtn.disabled = true;
            atcBtn.innerHTML = 'OUT OF<br>STOCK';
        }
    }

    _updatePopupNavState() {
        const swiper = this._popupSwiperEl?.swiper;
        if (!swiper) return;
        const up = this._popup.querySelector('.sc-sia-popup__nav--up');
        const down = this._popup.querySelector('.sc-sia-popup__nav--down');
        if (up) up.classList.toggle('is-hidden', swiper.isBeginning);
        if (down) down.classList.toggle('is-hidden', swiper.isEnd);
    }

    _getActivePopupVideo() {
        return this._popup?.querySelector(
            '.swiper-slide-active .sc-sia-popup__video'
        ) ?? null;
    }

    async _popupAddToCart() {
        if (!this._popupCurrentVariantId) return;

        const btn = this._popup.querySelector('.sc-sia-popup__atc-btn');
        const origHTML = btn.innerHTML;
        btn.disabled = true;
        btn.classList.add('is-loading');
        btn.innerHTML = `<span class="sc-sia-popup__atc-label">${origHTML}</span><span class="sc-sia-popup__atc-spinner" aria-hidden="true"></span>`;

        try {
            // Match the theme's cart-add pattern: FormData + sections from the cart element
            const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
            const sections = cart?.getSectionsToRender?.()
                .map(s => s.section || s.id).join(',') || 'cart-drawer,cart-icon-bubble';

            const formData = new FormData();
            formData.append('id', this._popupCurrentVariantId);
            formData.append('quantity', '1');
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);

            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/javascript',
                },
                body: formData,
            });

            const data = await res.json();
            if (data.status) throw new Error(data.description || 'Could not add to cart');

            this.closePopup();

            if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');

            if (cart?.renderContents) {
                cart.renderContents(data);
            }
        } catch (e) {
            console.error('SIA popup: add-to-cart failed', e);
        } finally {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.innerHTML = origHTML;
        }
    }

    disconnectedCallback() {
        if (this._popup) {
            this._popup.remove();
            this._popup = null;
        }
        if (this._popupEscHandler) {
            document.removeEventListener('keydown', this._popupEscHandler);
        }
    }
}

customElements.define('sc-see-it-in-action', ScSeeItInAction);
